import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  toastSuccess,
  toastError,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import service from '@Service';

/**
 * Inline campaign composer: creates an SMS OutboundMessage to an explicit set of
 * fishIds and sends immediately (sendTime = now). SMS-only — we can't guarantee
 * an email on file for every attendee/staff member. Shared by the attendee and
 * staff panels — render it when the parent's "Message…" toggle is open; the
 * parent owns the toggle and `onSent` closes it.
 */
export default function MessageComposer({
  fishIds,
  campaignTitle,
  recipientLabel = 'recipient',
  onSent,
}: {
  fishIds: string[];
  campaignTitle: string;
  recipientLabel?: string;
  onSent: () => void;
}) {
  const [senderId, setSenderId] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const { data: inboxesRaw } = useQuery({
    queryKey: ['inboxes', 'event-report-compose'],
    queryFn: () => service.getAllInboxes({}),
  });
  const inboxes: any[] = Array.isArray(inboxesRaw)
    ? inboxesRaw
    : (inboxesRaw?.data ?? []);
  // SMS-capable, non-fish inboxes (must have a phone number).
  const senders = inboxes.filter((ib) => ib.fishId == null && ib.phoneNumber);

  // Default the sender to the first valid inbox once they load.
  useEffect(() => {
    if (senders.length && !senders.some((s) => s.id === senderId)) {
      setSenderId(senders[0].id);
    }
  }, [senders, senderId]);

  const count = fishIds.length;
  const plural = count === 1 ? '' : 's';

  const send = async () => {
    if (!body.trim()) {
      toastError('Write a message first.');
      return;
    }
    setSending(true);
    try {
      const res = await service.createOutboundMessage({
        fishIds,
        method: 'SMS',
        messageBody: body,
        senderId: senderId || undefined,
        sendTime: new Date().toISOString(),
        campaignTitle,
      });
      if (res && res.ok === false) throw new Error(`status ${res.status}`);
      toastSuccess(`Message queued to ${count} ${recipientLabel}${plural}.`);
      onSent();
    } catch {
      toastError('Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mb-3 space-y-3 rounded-lg border border-zinc-200 bg-zinc-50 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-md bg-zinc-200 px-2 py-1.5 text-xs font-semibold tracking-wide text-zinc-600 uppercase">
          SMS
        </span>
        <select
          value={senderId}
          onChange={(e) => setSenderId(e.target.value)}
          className="min-w-0 flex-1 rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm"
        >
          <option value="">No sender (default)</option>
          {senders.map((ib) => (
            <option key={ib.id} value={ib.id}>
              {ib.phoneNumber}
              {ib.user?.fullDisplayName ? ` (${ib.user.fullDisplayName})` : ''}
            </option>
          ))}
        </select>
      </div>

      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={4}
        placeholder={`Message to ${count} ${recipientLabel}${plural}…`}
        className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm"
      />

      <div className="flex items-center justify-between">
        <span className="text-xs text-zinc-500">
          Sends now to all {count} {recipientLabel}
          {plural}.
        </span>
        <Button size="sm" onClick={send} disabled={sending}>
          {sending ? (
            <>
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              Sending…
            </>
          ) : (
            'Send'
          )}
        </Button>
      </div>
    </div>
  );
}
